import {
  EmojiPicker,
  useActiveEmoji,
  type EmojiPickerListCategoryHeaderProps,
  type EmojiPickerListComponents,
  type EmojiPickerListEmojiProps,
  type EmojiPickerListRowProps,
} from 'frimousse';
import {
  createContext,
  useContext,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { Popover } from '@/components/ui/popover';
import { pluralize } from '@/lib/date-utils';
import { cn } from '@/lib/utils';

import {
  EMOJI_POPOVER_GAP,
  EMOJI_POPOVER_HEIGHT,
  type PopoverPlacement,
} from './emojiPopoverPlacement';
import { readRecentEmojis, writeRecentEmoji } from './emojiRecents';

// Pinned the way the app's icon picker pins its own emoji tab, so every viewer's
// picker draws from the same emoji set
const EMOJI_DATA_URL = 'https://cdn.jsdelivr.net/npm/emojibase-data@16.0.3';

const TILE_CLASS =
  'flex size-7 items-center justify-center rounded text-base outline-none hover:bg-surface-hover focus-visible:bg-surface-hover data-[active]:bg-surface-hover';
const CHOSEN_TILE_CLASS =
  'bg-kind-act/[.08] ring-1 ring-inset ring-kind-act hover:bg-kind-act/[.08]';
const FULL_TILE_CLASS = 'opacity-40';

export type EmojiChoice = { chosen: readonly string[]; limit: number };

type TileState = { chosen: boolean; full: boolean };

const ChoiceContext = createContext<EmojiChoice | undefined>(undefined);

const tileState = (
  choice: EmojiChoice | undefined,
  emoji: string
): TileState => {
  const chosen = choice?.chosen.includes(emoji) ?? false;
  const full =
    choice !== undefined && !chosen && choice.chosen.length >= choice.limit;
  return { chosen, full };
};

const tileClass = ({ chosen, full }: TileState) =>
  cn(TILE_CLASS, chosen && CHOSEN_TILE_CLASS, full && FULL_TILE_CLASS);

function CategoryHeader({
  category,
  ...props
}: EmojiPickerListCategoryHeaderProps) {
  return (
    <div
      className="bg-surface px-1.5 pb-1 pt-2 text-[11px] text-foreground-muted"
      {...props}
    >
      {category.label}
    </div>
  );
}

function Row({ children, ...props }: EmojiPickerListRowProps) {
  return (
    <div {...props} className="grid grid-cols-8 gap-0.5 px-1.5">
      {children}
    </div>
  );
}

function EmojiCell({ emoji, ...props }: EmojiPickerListEmojiProps) {
  const choice = useContext(ChoiceContext);
  const state = tileState(choice, emoji.emoji);
  return (
    <button
      type="button"
      className={tileClass(state)}
      data-chosen={state.chosen ? '' : undefined}
      aria-pressed={choice ? state.chosen : undefined}
      aria-disabled={state.full || undefined}
      {...props}
    >
      {emoji.emoji}
    </button>
  );
}

const LIST_COMPONENTS: EmojiPickerListComponents = {
  CategoryHeader,
  Row,
  Emoji: EmojiCell,
};

// Where a key moves focus along the recents row; undefined leaves the key alone
function rowTarget(key: string, index: number, count: number) {
  if (key === 'ArrowLeft') return Math.max(0, index - 1);
  if (key === 'ArrowRight') return Math.min(count - 1, index + 1);
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return undefined;
}

// Read once per opening, so a pick never reorders the row under the pointer
function RecentRow({ onPick }: { onPick: (emoji: string) => void }) {
  const [recents] = useState(readRecentEmojis);
  const choice = useContext(ChoiceContext);
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const tiles = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>('button')
    );
    const index = tiles.indexOf(event.target as HTMLButtonElement);
    if (index < 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.currentTarget.parentElement
        ?.querySelector<HTMLInputElement>('input')
        ?.focus();
      return;
    }
    const target = rowTarget(event.key, index, tiles.length);
    if (target === undefined) return;
    event.preventDefault();
    tiles[target]?.focus();
  };
  return (
    <div
      role="toolbar"
      aria-label="Recent emoji"
      className="grid grid-cols-8 gap-0.5 border-b border-border p-1.5"
      onKeyDown={onKeyDown}
    >
      {recents.map((emoji) => {
        const state = tileState(choice, emoji);
        return (
          <button
            key={emoji}
            type="button"
            className={tileClass(state)}
            aria-pressed={choice ? state.chosen : undefined}
            aria-disabled={state.full || undefined}
            onClick={() => onPick(emoji)}
          >
            {emoji}
          </button>
        );
      })}
    </div>
  );
}

function SearchField({ onClose }: { onClose: () => void }) {
  const [search, setSearch] = useState('');
  const active = useActiveEmoji();
  return (
    <div className="border-b border-border p-1.5">
      <EmojiPicker.Search
        autoFocus
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' || search !== '' || active) return;
          event.preventDefault();
          onClose();
        }}
        placeholder="Search emoji"
        className="h-7 w-full rounded-md bg-surface-hover px-2 text-xs outline-none placeholder:text-foreground-muted"
      />
    </div>
  );
}

// The app's emoji picker: the same `frimousse` grid the icon picker's emoji tab
// draws, with the viewer's recent picks above the search. Given a `choice`, it
// picks several: each pick toggles one, chosen tiles are marked, and the popover
// stays open until dismissed
export function EmojiPickerPopover({
  open,
  onOpenChange,
  trigger,
  onSelect,
  choice,
  placement,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: ReactNode;
  onSelect: (emoji: string) => void;
  choice?: EmojiChoice;
  // A phone places the picker itself, clear of the keyboard; elsewhere it opens
  // under the trigger and flips on collision
  placement?: PopoverPlacement;
}) {
  const pick = (emoji: string) => {
    const state = tileState(choice, emoji);
    if (state.full) return;
    if (!state.chosen) writeRecentEmoji(emoji);
    onSelect(emoji);
  };
  const full = choice !== undefined && choice.chosen.length >= choice.limit;
  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Content
        align="start"
        side={placement?.side ?? 'bottom'}
        sideOffset={placement?.sideOffset ?? EMOJI_POPOVER_GAP}
        alignOffset={placement?.alignOffset ?? 0}
        avoidCollisions={placement === undefined}
        className="w-64 p-0"
      >
        <ChoiceContext.Provider value={choice}>
          <EmojiPicker.Root
            className="flex w-full flex-col"
            style={{ height: placement?.height ?? EMOJI_POPOVER_HEIGHT }}
            emojibaseUrl={EMOJI_DATA_URL}
            onEmojiSelect={(emoji) => pick(emoji.emoji)}
          >
            <RecentRow onPick={pick} />
            <SearchField onClose={() => onOpenChange(false)} />
            <EmojiPicker.Viewport className="min-h-0 flex-1 overflow-y-auto">
              <EmojiPicker.Loading className="p-3 text-xs text-foreground-subtle">
                Loading emoji…
              </EmojiPicker.Loading>
              <EmojiPicker.Empty className="p-3 text-xs text-foreground-subtle">
                No emoji found
              </EmojiPicker.Empty>
              <EmojiPicker.List
                className="py-1.5"
                components={LIST_COMPONENTS}
              />
            </EmojiPicker.Viewport>
            {choice ? (
              <p
                aria-live="polite"
                className="border-t border-border px-2 py-1.5 text-[11px] leading-4 text-foreground-muted"
              >
                <span className={cn(!full && 'invisible')}>
                  Up to {choice.limit} {pluralize(choice.limit, 'reaction')}
                </span>
              </p>
            ) : null}
          </EmojiPicker.Root>
        </ChoiceContext.Provider>
      </Popover.Content>
    </Popover.Root>
  );
}
